import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';

/**
 * A list of boxes: the card style every list page uses, inside a `ListCard`,
 * matching the internship cards on the public listings.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   ┃ [AB]  Title  (Status)                                        │
 *   ┃       subtitle                                               │
 *   ┃ (📍 Delft) (💼 Hybrid) (👥 1 position)                         │
 *   ┃ Two lines of description…                                    │
 *   ┃──────────────────────────────────────────────────────────────│
 *   ┃ meta (a date)              [Secondary] [More ⋯] [Main action]  │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * A stripe down the left edge carries the status colour. The actions sit in
 * the footer, in the same place on every card and every tab, so switching
 * status never moves them. Pass `BoxRow`s as children.
 */
export function BoxList({ children, className }) {
  return <ul className={cn('space-y-3 p-3 sm:p-4', className)}>{children}</ul>;
}

/** The stripe colour down each box's left edge. */
const ACCENTS = {
  brand: 'bg-brand-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  info: 'bg-info-500',
  neutral: 'bg-line',
};

/**
 * One box, laid out like an internship card on the public listings: the
 * avatar and name on top, the facts as chips beneath them, and the actions in
 * a footer under a hairline -- one card language across every list.
 *
 * - `leading`: the avatar or icon tile (see `BoxAvatar`).
 * - `title` + `to`: the name, linked when `to` is given.
 * - `badges`: status badges beside the title.
 * - `subtitle`: one muted line under the title (email, company…).
 * - `facts`: `[{ icon, text, tone? }]` shown as small chips; falsy entries are skipped.
 * - `description`: clamped to two lines.
 * - `children`: anything else, full width under the facts.
 * - `meta`: quiet text on the left of the footer (a category, a date).
 * - `actions`: buttons, on the right of the footer -- where Apply sits on an
 *   internship card.
 * - `corner`: the top-right corner, as an internship card keeps its bookmark
 *   there: a toggle (Save, Save to pool) or the actions that end the record
 *   (Withdraw, Close, Delete), kept apart from the everyday ones in the footer.
 * - `accent`: the stripe tone down the left edge
 *   (`brand | success | warning | danger | info | neutral`).
 * - `compact`: one slim line with the actions on the right, for long lists of
 *   small records (reference data) where a footer per row would be all chrome.
 * - `muted`: the record is on hold (a candidate whose account is suspended):
 *   the avatar, subtitle and body fade back while the title, badges and
 *   actions stay legible -- it is still there, and still opens.
 * - A fact may carry `key` when its `text` is a node rather than a string.
 */
export function BoxRow({
  leading,
  title,
  to,
  badges,
  subtitle,
  facts = [],
  description,
  meta,
  actions,
  corner,
  accent = 'neutral',
  compact = false,
  muted = false,
  className,
  children,
}) {
  const shownFacts = facts.filter(Boolean);
  const fade = muted && 'opacity-55';

  const heading = (
    <div className="flex min-w-0 flex-1 items-start gap-3.5">
      {muted && leading ? <span className="shrink-0 opacity-55">{leading}</span> : leading}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {to ? (
            <Link
              to={to}
              className="text-ink hover:text-brand-700 min-w-0 text-[15px] leading-snug font-semibold break-words transition"
            >
              {title}
            </Link>
          ) : (
            <span className="text-ink min-w-0 text-[15px] leading-snug font-semibold break-words">
              {title}
            </span>
          )}
          {badges}
        </div>
        {subtitle && (
          <div className={cn('text-ink-muted mt-0.5 min-w-0 text-[13px] break-words', fade)}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  const factChips = shownFacts.length > 0 && (
    <ul className={cn('flex flex-wrap gap-1.5', compact ? 'mt-2' : 'mt-3.5')}>
      {shownFacts.map((fact, index) => (
        <li
          key={fact.key ?? (typeof fact.text === 'string' ? fact.text : index)}
          className={cn(
            'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs wrap-anywhere',
            fact.tone === 'success'
              ? 'bg-success-50 text-success-700'
              : fact.tone === 'warning'
                ? 'bg-warning-50 text-warning-800'
                : fact.tone === 'danger'
                  ? 'bg-danger-50 text-danger-700'
                  : fact.tone === 'brand'
                    ? 'bg-brand-50 text-brand-700'
                    : 'bg-surface-muted text-ink-muted ring-line ring-1 ring-inset',
          )}
        >
          {fact.icon && <Icon name={fact.icon} size={12} className="shrink-0" />}
          {fact.text}
        </li>
      ))}
    </ul>
  );

  const body = (
    <div className={cn(fade || undefined)}>
      {factChips}
      {description && (
        <p className="text-ink-muted mt-2.5 line-clamp-2 text-[12px] leading-5">{description}</p>
      )}
      {children}
    </div>
  );

  return (
    <li
      className={cn(
        // No overflow-hidden: it cut off the hover names of the corner actions.
        // The stripe carries its own rounded corners instead.
        'border-line bg-surface rounded-card shadow-card hover:border-brand-200 @container relative border transition duration-200 hover:shadow-md',
        muted && 'bg-surface-muted/70',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-0 -left-px w-[3px] rounded-l-[var(--radius-card)]',
          ACCENTS[accent] ?? ACCENTS.neutral,
        )}
      />
      {compact ? (
        <div className="flex flex-col gap-2.5 p-3 pl-4 @3xl:flex-row @3xl:items-center">
          <div className="min-w-0 flex-1">
            {heading}
            {body}
          </div>
          {(actions || corner) && (
            <div className="flex shrink-0 flex-wrap items-center gap-1">
              {actions}
              {corner}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 pl-5 sm:p-5 sm:pl-6">
          {corner ? (
            // The corner's labelled buttons drop under the name on a narrow
            // card instead of squeezing it to a sliver.
            <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
              <div className="flex min-w-0 grow basis-56">{heading}</div>
              <div className="-mt-1 -mr-1 ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1">
                {corner}
              </div>
            </div>
          ) : (
            heading
          )}
          {body}
          {/* The footer under a hairline, as on an internship card: details on
              the left, the actions on the right where Apply sits. */}
          {(actions || meta) && (
            <div className="border-line text-ink-muted mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3 text-xs">
              <div className="min-w-0">{meta}</div>
              {actions && (
                <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
                  {actions}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The right of a card's footer: labelled small buttons, secondary ones first
 * and the main action last (rightmost). On a phone they become an even
 * grid across the card -- equal buttons, the main one still last -- rather
 * than a ragged right-aligned wrap: two per row with an odd one out spanning
 * it, except that three share one row on all but the narrowest phones.
 */
export function CardActions({ children, className }) {
  return (
    <div
      className={cn(
        'grid w-full grid-cols-2 gap-2 *:w-full *:justify-center *:px-2 *:whitespace-nowrap [&>*:last-child:nth-child(odd)]:col-span-2',
        'min-[400px]:has-[>:nth-child(3):last-child]:grid-cols-3 min-[400px]:[&>*:nth-child(3):last-child]:col-span-1',
        'sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end sm:*:w-auto',
        className,
      )}
    >
      {children}
    </div>
  );
}

const AVATAR_TONES = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-100',
  solid: 'bg-brand-600 text-white ring-brand-600',
  success: 'bg-success-50 text-success-700 ring-success-100',
  warning: 'bg-warning-50 text-warning-800 ring-warning-100',
  danger: 'bg-danger-50 text-danger-700 ring-danger-100',
  neutral: 'bg-surface-muted text-ink-muted ring-line',
};

/** The tile at the start of a box: an `imageUrl` (a logo), an `icon`, or initials from `name`. */
export function BoxAvatar({ name, icon, imageUrl, tone = 'brand', round = false, size = 'md' }) {
  const initials = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center overflow-hidden text-xs font-semibold ring-1 ring-inset',
        size === 'sm' ? 'size-8' : 'size-10',
        round ? 'rounded-full' : 'rounded-lg',
        AVATAR_TONES[tone] ?? AVATAR_TONES.brand,
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="size-full object-cover" />
      ) : icon ? (
        <Icon name={icon} size={size === 'sm' ? 15 : 17} />
      ) : (
        initials || '?'
      )}
    </span>
  );
}
